import React, { useRef, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Label, Radio } from "theme-ui";
import ServerAutoSuggest from "../ServerAutoSuggest";
import { profileApiCachedUrl } from "../../../service-wrappers/profile-api";

const dflt = [];
const getMandatoryEnv = require("../../../utils/getMandatoryEnv");

const { mySrcmConfig } = getMandatoryEnv(["mySrcmConfig"]);

const fetchCitySrcm = async (cityId) => {
  const response = await fetch(
    `${mySrcmConfig?.profileServer}/api/v2/cities/?id=${cityId}`
  );
  const data = await response.json();

  const city = data?.results[0];
  return {
    id: city?.id,
    name: city?.name,
    country: city?.country?.name || "",
    state: city?.state?.name || city?.country?.name || "",
    country_id: city?.country?.id || null,
    state_id: city?.state?.id || null,
    complete_name: city?.complete_name,
    type: "city",
  };
};

const getLocationData = async (id, value, isPostalCode) => {
  if (typeof id === "string" && id.endsWith("^^")) {
    const s = id.split("^^");
    return Promise.resolve(
      isPostalCode
        ? { id: s[0], pincode: s[0], country: "", type: "zip" }
        : { id: s[0], name: s[0], state: "", country: "", type: "city" }
    );
  }
  if (isPostalCode) {
    return Promise.resolve({
      id: id || "",
      pincode: value || "",
      country: "",
      type: "zip",
    });
  }
  return fetchCitySrcm(id);
};
const CityZipAPI = ({
  city,
  includeOnly,
  edit,
  enableCountryFilter,
  onChange,
  ...restprops
}) => {
  const [config, setConfig] = useState(null);
  const [isPostal, setIsPostal] = useState(false);
  const [error, setError] = useState("");
  const userLoginCountry =
    typeof window !== "undefined"
      ? localStorage.getItem("userLoginCountry")?.toLowerCase() || ""
      : "";
  const [selectedFilter, setSelectedFilter] = useState(
    enableCountryFilter && userLoginCountry ? "india" : "all"
  );

  const filterCountry = useRef(
    enableCountryFilter && userLoginCountry ? "india" : "all"
  );
  const validateInput = (value) => {
    if (filterCountry.current === "india") {
      if (!/^(?:[A-Za-z0-9,\-() ]+)$/.test(value)) {
        return "Only alphabets or numerics are allowed.";
      }
    } else if (!/^[A-Za-z,\-() ]+$/.test(value)) {
      return "Only alphabets are allowed.";
    }
    return "";
  };
  const capitalizeWords = (str) =>
    str.replace(/\b\w/g, (char) => char.toUpperCase());
  const updateCityLabel = useCallback((countryVal) => {
    if (typeof document !== "undefined") {
      const cityLabelElement = document.getElementById("city_label");
      if (cityLabelElement) {
        cityLabelElement.innerHTML = countryVal
          ? `Search Postal code (or) City/Locality from ${capitalizeWords(
              countryVal
            )}`
          : `Search Cities from All Countries`;
      }
    }
  }, []);

  useEffect(() => {
    const initializeFilterCountry = async () => {
      if (restprops.value && !isPostal) {
        const country = (await fetchCitySrcm(restprops.value))?.country || "";
        filterCountry.current =
          country.toLowerCase() === "india" ? "india" : "";
        setSelectedFilter(filterCountry.current || "all");
        updateCityLabel(filterCountry.current);
      }
      updateCityLabel(filterCountry.current);
    };

    initializeFilterCountry();
  }, [
    enableCountryFilter,
    isPostal,
    restprops.value,
    updateCityLabel,
    userLoginCountry,
  ]);
  function transformCompleteName(completeName) {
    const parts = [
      ...new Set(completeName?.split("/").map((part) => part?.trim())),
    ];
    return parts.reverse().join(", ");
  }
  const updateConfig = useCallback(() => {
    const getConfigsObj = {
      dflt: includeOnly ? dflt.filter(includeOnly) : dflt,
      fetch: (id, value) => getLocationData(id, value, /^[0-9]+$/.test(value)),
      getSuggestionValue: (suggestion) => {
        if (suggestion?.pincode) {
          return suggestion?.pincode;
        }
        if (filterCountry.current === "india") {
          return transformCompleteName(suggestion?.complete_name);
        }
        return `${
          suggestion.name
        }${`, ${suggestion.state}`}${`, ${suggestion.country}`}`;
      },

      renderSuggestion: (suggestion) => {
        if (suggestion?.pincode) {
          return suggestion?.pincode;
        }
        if (filterCountry.current === "india") {
          return transformCompleteName(suggestion?.complete_name);
        }
        return `${
          suggestion.name
        }${`, ${suggestion.state}`}${`, ${suggestion.country}`}`;
      },

      api: (itrimValue) => {
        const errorMessage = validateInput(itrimValue);
        if (errorMessage) {
          setError(errorMessage);
          return "";
        }
        setError("");
        const isPostalCode = /^[0-9]+$/.test(itrimValue);
        let apiUrl = "";
        if (filterCountry.current === "india") {
          if (isPostalCode) {
            apiUrl = `${mySrcmConfig?.profileServer}/api/v2/pincodes/?pincode__startswith=${itrimValue}`;
          } else {
            apiUrl = `${mySrcmConfig?.profileServer}/api/v2/cities/?city_type=place&name__istartswith=${itrimValue}`;
          }
        } else {
          apiUrl = profileApiCachedUrl(
            "cities-prefix",
            itrimValue.substring(0, 3)
          );
        }

        return apiUrl;
      },

      data2resultsAsync: async (
        data,
        itrimValue,
        getSuggestionValue,
        dflt_in
      ) => {
        const isPostalCode = /^[0-9]+$/.test(itrimValue);
        setIsPostal(isPostalCode);
        if (isPostalCode) {
          return [...data.results];
        }
        const dfltIds = dflt_in.map((r) => r.id);
        const filterFn = (f, itrimValue1) =>
          getSuggestionValue(f).toLowerCase().startsWith(itrimValue1);
        const cur_results = data.results
          .filter(
            (f) =>
              (!includeOnly || includeOnly(f)) &&
              f.active &&
              filterFn(f, itrimValue) &&
              !dfltIds.includes(f.id)
          )
          .filter((i, idx) => idx < 20);
        const dflt_results = dflt_in.filter((f) => filterFn(f, itrimValue));
        return [...dflt_results, ...cur_results];
      },
    };
    setConfig(getConfigsObj);
  }, [includeOnly]);

  useEffect(() => {
    updateConfig();
  }, [updateConfig]);

  const labelStyle = {
    color: "#000",
    fontSize: "14px",
    fontStyle: "italic",
    width: "auto",
    cursor: "pointer",
    display: "inline-flex",
    marginLeft: "0.8em",
    position: "relative",
  };

  const onChangeOption = (value) => {
    const isAllSelected = value === "all";
    filterCountry.current = isAllSelected ? "" : userLoginCountry;
    setSelectedFilter(isAllSelected ? "all" : "india");
    updateCityLabel(filterCountry.current);
    updateConfig();
    setError("");
    if (onChange) {
      onChange(undefined, null);
    }
  };

  return (
    <>
      {config && (
        <div key={filterCountry.current}>
          <ServerAutoSuggest
            {...restprops}
            onChange={onChange}
            config={config}
            enableScroll
            maxHeight="300px"
          />
          {error && (
            <span style={{ color: "red", fontSize: "12px" }}>{error}</span>
          )}
        </div>
      )}
      {edit && enableCountryFilter && userLoginCountry && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Label sx={labelStyle}>
            <Radio
              name="allowFilter"
              value="all"
              onChange={() => onChangeOption("all")}
              checked={selectedFilter === "all"}
            />
            All Countries
          </Label>
          <Label sx={labelStyle}>
            <Radio
              name="allowFilter"
              value="user-country"
              onChange={() => onChangeOption("india")}
              checked={selectedFilter === "india"}
            />
            {userLoginCountry.charAt(0).toUpperCase() +
              userLoginCountry.slice(1)}
          </Label>
        </div>
      )}
    </>
  );
};

CityZipAPI.propTypes = {
  city: PropTypes.objectOf(PropTypes.any),
  edit: PropTypes.bool,
  includeOnly: PropTypes.func,
  onChange: PropTypes.func,
  enableCountryFilter: PropTypes.bool,
};

CityZipAPI.defaultProps = {
  city: { id: "", name: "" },
  edit: false,
  includeOnly: null,
  onChange: null,
  enableCountryFilter: false,
};

export default CityZipAPI;
